import React, {
  FC,
  useCallback,
  useState,
  ReactElement,
  useMemo,
  useEffect,
  useRef,
  Ref,
  ReactNode,
} from "react";

import { Placement } from "@popperjs/core";
import classNames from "classnames";

import { OptionContent } from "./OptionContent/OptionContent";
import {
  StyledSelectForm,
  StyledSelectInput,
  StyledCheckbox,
  StyledRadioButton,
  StyledStartAdornment,
  StyledEndAdornment,
} from "./Styles";
import useForkRef from "../../utils/useForkRef";
import { Autocomplete, isOptionGroup } from "../Autocomplete";
import { IAutocompleteProps } from "../Autocomplete/Autocomplete";
import { Menu } from "../Menu/Menu";
import { IOptionItemProps } from "../Select/Option";
import { IOptionGroupProps } from "../Select/OptionGroup";

export interface ElementWithRef<T> extends ReactElement {
  ref?: Ref<T>;
}

export interface ISelectProps
  extends Omit<
    IAutocompleteProps,
    | "handleChange"
    | "inputEl"
    | "handleEmptyAction"
    | "clearInputOnSelect"
    | "mixedOptions"
  > {
  /** Menu width mode */
  mode?: "normal" | "wider" | "tiny";
  /** Set select target element */
  target?: ElementWithRef<Element>;
  /** Set Select position */
  position?: Placement;
  /** onChange callback */
  onChange?: (e: (string | number)[] | string | number) => void;
  /** Placeholder for Select input */
  placeholder?: string;
  /** Function that triggers when empty is clicked */
  emptyAction?: (e: string | undefined) => void;
  /** Removes input from Select */
  disableSearch?: boolean;
  /** Close menu on select */
  forceCloseMenu?: boolean;
  /** Set select classNames */
  selectClassName?: string;
  /** Callback called when the Select menu is opened */
  onSelectOpen?: () => void;
  /** Callback called when the Select menu is closed */
  onSelectClose?: () => void;
  /** Node element on the left side */
  startAdornment?: ReactNode;
  /** Node element on the right side */
  endAdornment?: ReactNode;
  /** Focus search input on inline select */
  focusInput?: boolean;
  /** Mixed options values */
  mixed?: (string | number)[];
}

export const Select: FC<ISelectProps> = ({
  type = "single",
  mode,
  target,
  options,
  selected = [],
  mixed = [],
  position = "bottom-start",
  onChange = () => null,
  placeholder,
  emptyAction = (): null => null,
  disableSearch = false,
  forceCloseMenu,
  selectClassName,
  keepSameOptionsOrder = false,
  onSelectOpen,
  onSelectClose,
  renderOption,
  handleDefaultOptionChange,
  preselectDefaultValue = type === "single",
  startAdornment,
  endAdornment,
  focusInput = false,
  ...prop
}) => {
  if (disableSearch && (startAdornment || endAdornment)) {
    console.warn("Adornments are not visible when search bar is hidden");
  }
  const childNode = useRef<HTMLInputElement | null>();
  const elementRef = useRef<HTMLInputElement | null>(null);

  const handleRef = useForkRef(childNode, elementRef);

  const Tag = !target ? "div" : Menu;
  const formRef = useRef<HTMLFormElement | null>(null);

  const [open, setOpen] = useState(false);

  const selectedOptions = useMemo(
    () => (selected instanceof Array ? selected : [selected]),
    [selected]
  );

  const selectOptions = useMemo(() => {
    if (!options) {
      return [];
    }
    return options;
  }, [options]);

  const allOptionValues = useMemo(() => {
    return selectOptions.reduce(
      (
        acc: (string | number)[],
        option: IOptionGroupProps | IOptionItemProps
      ) => {
        if (!isOptionGroup(option)) {
          return [...acc, option.id];
        }
        const allGroupOptions = option.options.map((option) => option.id);
        return [...acc, ...allGroupOptions];
      },
      []
    );
  }, [selectOptions]);

  const mixedOptions = useRef(type === "multiple" && mixed.length ? mixed : []);

  const removeFromMixedOptions = useCallback(
    (idsToRemove: (string | number)[]) => {
      mixedOptions.current = mixedOptions.current.filter(
        (mixedOption) => !idsToRemove.includes(mixedOption)
      );
    },
    []
  );

  const isAllOptionsChecked = useMemo(
    () => allOptionValues.every((option) => selectedOptions.includes(option)),
    [allOptionValues, selectedOptions]
  );

  const someOptionsChecked = useMemo(
    () => allOptionValues.some((option) => selectedOptions.includes(option)),
    [allOptionValues, selectedOptions]
  );

  useEffect(() => {
    if ((open && target) || (!target && focusInput)) {
      childNode?.current?.focus();
    }
  }, [focusInput, open, target]);

  const handleChange = useCallback(
    (selectedValue) => {
      if (forceCloseMenu) {
        if (typeof onSelectClose === "function") onSelectClose();
        setOpen(false);
      }
      if (
        selectedValue !== undefined &&
        selectedValue !== selected &&
        !(selectedValue instanceof Array && selectedValue.includes(undefined))
      ) {
        onChange(selectedValue);
        type === "multiple" &&
          mixedOptions.current.length &&
          removeFromMixedOptions(selectedValue);
      }
    },
    [
      forceCloseMenu,
      selected,
      onSelectClose,
      onChange,
      type,
      removeFromMixedOptions,
    ]
  );

  const onClose = useCallback(() => {
    setOpen(false);
    if (onSelectClose) {
      onSelectClose();
    }
  }, [onSelectClose]);

  const onOpen = useCallback(() => {
    setOpen(true);
    if (onSelectOpen) {
      onSelectOpen();
    }
  }, [onSelectOpen]);

  const props = useMemo(() => {
    if (target) {
      return {
        menuClassName: classNames("c-select", selectClassName),
        onClose,
        onOpen,
        target,
        mode,
        open,
        position,
        onPopperOpen: () => elementRef?.current?.focus(),
      };
    }
    return { className: classNames("c-select", selectClassName) };
  }, [target, selectClassName, onClose, onOpen, mode, open, position]);

  const handleEmptyAction = useCallback(
    (value) => {
      emptyAction(value);
      if (forceCloseMenu) {
        setOpen(false);
      }
      formRef.current?.reset();
    },
    [emptyAction, forceCloseMenu]
  );

  const handleDefaultOptionChangeCallback = useCallback(() => {
    if (type === "multiple") {
      if (selectedOptions.length === allOptionValues.length) {
        if (typeof handleDefaultOptionChange === "function") {
          handleDefaultOptionChange();
        } else {
          onChange([]);
        }
      } else {
        if (typeof handleDefaultOptionChange === "function") {
          handleDefaultOptionChange();
        } else {
          onChange(allOptionValues);
        }
      }
    } else {
      if (typeof handleDefaultOptionChange === "function") {
        handleDefaultOptionChange();
      } else {
        onChange("");
      }
    }
    if (forceCloseMenu) {
      setOpen(false);
    }
  }, [
    onChange,
    type,
    forceCloseMenu,
    selectedOptions.length,
    allOptionValues,
    handleDefaultOptionChange,
  ]);

  const handleRenderOption = useCallback(
    (option, props) => {
      if (typeof renderOption === "function") {
        return renderOption(option, props);
      }
      return (
        <>
          <OptionContent
            imageUrl={option.image}
            color={option.color}
            textColor={option.textColor}
            name={option.name}
            additionalInfo={option.additionalInfo}
          />
          {option.id === null ? (
            type === "multiple" ? (
              <StyledCheckbox
                checked={isAllOptionsChecked}
                mixed={someOptionsChecked && !isAllOptionsChecked}
                {...props}
              />
            ) : (
              <StyledRadioButton
                checked={selectedOptions.length < 1 || !selectedOptions[0]}
                {...props}
              />
            )
          ) : type === "multiple" ? (
            <StyledCheckbox
              mixed={mixedOptions.current.includes(option.id)}
              {...props}
            />
          ) : (
            <StyledRadioButton {...props} />
          )}
        </>
      );
    },
    [
      isAllOptionsChecked,
      renderOption,
      type,
      someOptionsChecked,
      selectedOptions,
    ]
  );

  return (
    <Tag {...props}>
      <div>
        {!disableSearch ? (
          <StyledSelectForm ref={formRef}>
            {startAdornment ? (
              <StyledStartAdornment>{startAdornment}</StyledStartAdornment>
            ) : null}
            <StyledSelectInput ref={handleRef} placeholder={placeholder} />
            {endAdornment ? (
              <StyledEndAdornment>{endAdornment}</StyledEndAdornment>
            ) : null}
          </StyledSelectForm>
        ) : null}
        <Autocomplete
          {...prop}
          clearInputOnSelect
          inputEl={childNode}
          handleEmptyAction={handleEmptyAction}
          selected={selected}
          handleChange={handleChange}
          renderOption={handleRenderOption}
          options={options}
          mixedOptions={mixedOptions.current}
          handleDefaultOptionChange={handleDefaultOptionChangeCallback}
          type={type}
          preselectDefaultValue={preselectDefaultValue}
          keepSameOptionsOrder={keepSameOptionsOrder}
        />
      </div>
    </Tag>
  );
};

Select.displayName = "Select";
